import type { AudioSettings, AudioVolumeId } from './types/sound';

/** Every volume setting, in the order the settings pop-over lists them. */
export const AUDIO_VOLUME_IDS: readonly AudioVolumeId[] = ['master', 'music', 'ambience', 'effects', 'ui'];

/** Audio settings for an account that has never changed one. */
export function createDefaultAudioSettings(): AudioSettings {
    return {
        master: 0.8,
        music: 0.7,
        ambience: 0.7,
        effects: 0.8,
        ui: 0.6,
        isMuted: false,
    };
}

/**
 * Validates audio settings (e.g. from a PATCH body or a stored setting). A
 * setting left out keeps its default; any volume present must be a number
 * from 0 to 1 and `muted` a boolean, or the result is undefined.
 */
export function parseAudioSettings(value: unknown): AudioSettings | undefined {
    if (!value || typeof value !== 'object') {
        return undefined;
    }
    const candidate = value as Partial<Record<AudioVolumeId | 'isMuted', unknown>>;
    const defaults = createDefaultAudioSettings();
    const volumes = { ...defaults } as Record<AudioVolumeId, number>;
    for (const id of AUDIO_VOLUME_IDS) {
        const volume = candidate[id];
        if (volume === undefined) {
            continue;
        }
        if (typeof volume !== 'number' || !Number.isFinite(volume) || volume < 0 || volume > 1) {
            return undefined;
        }
        volumes[id] = volume;
    }
    const muted = candidate.isMuted ?? defaults.isMuted;
    if (typeof muted !== 'boolean') {
        return undefined;
    }
    return { ...volumes, isMuted: muted };
}
